/**
 * One bottle, mid-research: the crop, the best guess at what it is, and a pill
 * per specialist that lights up as its branch reports.
 */
import { STAGES } from '../lib/types'
import type { BottleCard, Stage, WineMatch } from '../lib/types'
import type { StageState } from '../hooks/useScan'

export default function BottleTile({
  card,
  url,
  stages,
  match,
  collapsedInto,
}: {
  card: BottleCard
  url?: string
  stages: Partial<Record<Stage, StageState>>
  match?: WineMatch
  collapsedInto?: string
}) {
  const name =
    match?.displayName ??
    [card.producerGuess, card.cuveeGuess, card.vintageGuess].filter(Boolean).join(' ') ??
    'Reading the label…'

  return (
    <article
      className={`card flex gap-3 p-3 transition ${collapsedInto ? 'opacity-45' : ''}`}
    >
      <div className="h-24 w-16 shrink-0 overflow-hidden rounded-lg bg-cellar-700">
        {url ? (
          <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="h-full w-full animate-pulse bg-white/5" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <h3 className="min-w-0 flex-1 truncate text-sm font-medium text-stone-100">
            {name || 'Reading the label…'}
          </h3>
          {match?.isNew === false && (
            <span className="pill bg-amber-400/15 text-amber-200" title={match.matchMethod}>
              seen before
            </span>
          )}
          {match?.isNew && <span className="pill bg-emerald-400/15 text-emerald-200">new</span>}
        </div>

        {collapsedInto ? (
          <p className="mt-1 text-xs text-stone-500">
            The same bottle as {collapsedInto} — merged.
          </p>
        ) : (
          <p className="mt-0.5 truncate text-xs text-stone-500">
            {card.legibility === 'partial' ? 'partly obscured · ' : ''}
            {card.colour ?? ''}
            {match?.priorSightings?.length
              ? ` · last seen ${new Date(match.priorSightings[0]?.seen_at ?? '').toLocaleDateString()}`
              : ''}
          </p>
        )}

        <div className="mt-2 flex flex-wrap gap-1">
          {STAGES.map((stage) => {
            const state = stages[stage] ?? 'waiting'
            return (
              <span
                key={stage}
                className={`pill border ${
                  state === 'done'
                    ? 'border-wine-500/50 bg-wine-700/30 text-wine-100'
                    : state === 'running'
                      ? 'animate-pulse border-white/20 text-stone-200'
                      : 'border-white/8 text-stone-600'
                }`}
              >
                {stage}
              </span>
            )
          })}
        </div>
      </div>
    </article>
  )
}
